import { useState } from 'react';
import { useFormikContext } from 'formik';
import { Plus, Trash2 } from 'lucide-react';

import { createPersistedStore } from '@@/datatables/types';
import { Button } from '@@/buttons';
import { EditableDatatable } from '@@/datatables/editable/EditableDatatable';
import { useTableState } from '@@/datatables/useTableState';

import {
  KubernetesNetworkSecurityFormValues,
  NetworkSecurityRule,
} from './types';
import { useColumns } from './useColumns';
import { CreateNetworkSecurityRuleModal } from './CreateNetworkSecurityRuleModal';

const accessStorageKey = 'k8s-network-security-egress-table';
const accessSettingsStore = createPersistedStore(accessStorageKey);

export function EgressRulesDatatable() {
  const {
    values: { egressRules = [] },
    setFieldValue,
  } = useFormikContext<KubernetesNetworkSecurityFormValues>();

  const tableState = useTableState(accessSettingsStore, accessStorageKey);

  const [isRuleAddModalOpen, setIsRuleAddModalOpen] = useState(false);

  return (
    <>
      <EditableDatatable
        title="Egress Rules"
        dataset={egressRules}
        columns={useColumns('egress')}
        acceptRow={() => {}}
        revertRow={() => {}}
        getRowId={(row) => `${row.name}`}
        settingsManager={tableState}
        renderTableActions={(selectedRows) => (
          <>
            <Button
              icon={Plus}
              color="primary"
              size="small"
              onClick={() => setIsRuleAddModalOpen(true)}
              data-cy="k8s-network-security-egress-datatable-add-rule-button"
              className="!ml-0"
            >
              Add Egress Rule
            </Button>
            <Button
              color="dangerlight"
              size="small"
              icon={Trash2}
              onClick={() => handleRemoveRules(selectedRows)}
              data-cy="k8s-network-security-egress-datatable-remove-rule-button"
              disabled={selectedRows.length === 0}
            >
              Remove
            </Button>
          </>
        )}
        data-cy="k8s-network-security-egress-datatable"
      />
      <CreateNetworkSecurityRuleModal
        ruleKind="egress"
        isOpen={isRuleAddModalOpen}
        onClose={() => setIsRuleAddModalOpen(false)}
      />
    </>
  );

  function onChange(rules: NetworkSecurityRule[]) {
    return setFieldValue('egressRules', rules);
  }

  function handleRemoveRules(selected: NetworkSecurityRule[]) {
    const idsToRemove = new Set(selected.map((item) => `${item.name}`));
    const newRuleList = egressRules.filter(
      (access) => !idsToRemove.has(`${access.name}`)
    );
    onChange(newRuleList);
  }
}
